<template>
  <div class="kyuukei">
    <h3>休憩未取得申請</h3>
    <template v-if="form.kyuukei_jikangai">
      <div class="item">
        <b class="required">休憩未取得勤務日</b>
        <date-picker v-model="form.kyuukei_jikangai.kyuukei_jikangai_date"
          :error="getError(form.kyuukei_jikangai, 'kyuukei_jikangai_date')"></date-picker>
      </div>

      <div class="item">
        <koyouKeiyakuTimeSelect v-if="isInit" :errors="form.errors"
          :targetDate="form.kyuukei_jikangai.kyuukei_jikangai_date" :koyouKeiyakuId="koyouKeiyakuId"
          :workTimeId="workTimeId" @change="changeKoyouKeiyakuTime">
        </koyouKeiyakuTimeSelect>
      </div>

      <div class="item">
        <b class="required">作業時間</b>
        <time-picker v-model="form.kyuukei_jikangai.kyuukei_jikangai_times" :startTime="{ hours: 0, minutes: 0 }"
          :error="getError(form.kyuukei_jikangai, 'kyuukei_jikangai_times')"
          :minTime="{ hours: 0, minutes: 15, seconds: 0 }"
          :maxTime="{ hours: 2, minutes: 0, seconds: 0 }"></time-picker>
      </div>

      <div class="item">
        <b class="required">業務内容（詳細に）</b>
        <input-textarea v-model="form.kyuukei_jikangai.gyoumu_notes"
          :error="getError(form.kyuukei_jikangai, 'gyoumu_notes')"></input-textarea>
      </div>

      <div class="item">
        <b>備考</b>
        <input-textarea v-model="form.notes" :error="getError(form, 'notes')"></input-textarea>
      </div>
    </template>
  </div>
</template>

<script lang="ts">

import { defineComponent, nextTick, onMounted, ref, Ref, toRefs } from 'vue';
import { useForm } from '@/composable/form/form';
import { Shinsei, ShinseiKoyouKeiyakuTime } from '@/types/Shinsei';
import { KoyouKeiyakuTimeSelectEmitData, SyuugyouBasyoAndKoyouKeiyakuOption } from '@/types/SyuugyouBasyo';
import KoyouKeiyakuTimeSelect from "@/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue"
import { Logger } from '@/utils/Logger';

export default defineComponent({
  props: {
    form: {
      type: Object as () => Shinsei,
      default: null
    },
  },
  emits: {
    changeKoyouKeiyakuTime: (selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null, selectedKoyouKeiyakuTimeOption: ShinseiKoyouKeiyakuTime | null) => true,
  },
  setup(props, context) {
    // エラー取得
    const { getError } = useForm()
    const { form } = toRefs(props)

    // 編集中の就業場所（雇用契約）と就業時間
    const workTimeId: Ref<number | null> = ref(null)
    const koyouKeiyakuId: Ref<number | null> = ref(null)

    const isInit = ref(false)

    onMounted(async () => {
      Logger.debug('----------------------------\n休憩未取得申請コンポーネントの初期化開始')
      Logger.debug('休憩未取得申請 onMounted', { '申請': form.value })
      nextTick(() => {
        Logger.debug('休憩未取得申請 onMounted->nextTick', { '申請': form.value })
        if (!form.value.kyuukei_jikangai) {
          Logger.debug('新規作成モードの初期化を行います')
          form.value.kyuukei_jikangai = {
            kyuukei_jikangai_date: null,
            kyuukei_jikangai_times: null,
            gyoumu_notes: null
          }
        } else {
          Logger.debug('編集モードの初期化を行います')
          workTimeId.value = form.value.shinsei_koyou_keiyaku_time.length > 0 ? form.value.shinsei_koyou_keiyaku_time[0].koyou_keiyaku_time_id : null
          koyouKeiyakuId.value = form.value.koyou_keiyaku_id ?? null
          Logger.debug('休憩未取得申請より就業場所（雇用契約）と就業時間を初期化しました', { '雇用契約ID': koyouKeiyakuId.value, '就業時間ID': workTimeId.value })
        }
        isInit.value = true
        Logger.debug('休憩未取得申請コンポーネントの初期化完了\n----------------------------')
      })
    })

    // 就業場所・就業時間が変更された場合に走る処理
    // 引数は以下コンポーネントのemits.changeに準拠
    // src/resources/ts/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue
    const changeKoyouKeiyakuTime = (emitData: KoyouKeiyakuTimeSelectEmitData) => {
      if (!form.value.kyuukei_jikangai) return

      let koyouKeiyakuTime: ShinseiKoyouKeiyakuTime | null = null

      // 就業時間に関する情報のセット
      if (emitData.selectedKoyouKeiyakuTimeOption) {
        koyouKeiyakuTime = {
          shinsei_id: form.value.shinsei_id,
          koyou_keiyaku_time_id: emitData.selectedKoyouKeiyakuTimeOption.val,
          target_date: form.value.kyuukei_jikangai.kyuukei_jikangai_date,
          tsuujouTime: null,
          isHannichi: null,
          canOnlyAmKinmu: null,
          canOnlyPmKinmu: null,
        }
      }

      // 就業場所に関する情報をemitする（申請オブジェクトの変更等は親に移譲する）
      context.emit('changeKoyouKeiyakuTime', emitData.selectedSyuugyouBasyo, koyouKeiyakuTime);
    }

    return {
      form,
      getError,
      changeKoyouKeiyakuTime,
      koyouKeiyakuId,
      workTimeId,
      isInit
    }
  },
  components: {
    'koyouKeiyakuTimeSelect': KoyouKeiyakuTimeSelect
  }
})

</script>
<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>
